import { useMutation, useQueryClient } from "@tanstack/react-query";
import { quickAccessUpsertMutation, type TQuickAccess } from "../../../market-grid/keyboard/useQuickAccess";
import { FileMagnifyingGlassIcon } from "../../../icons/FileMagnifyingGlassIcon";
import { useGraphQLClient } from "@package/graphql/react";
import type { MenuConfig } from "../../types";
import { any, selection, recipes } from "../../disabled-helpers";
import { useUserId } from "../../../../context/auth/hooks";

export const useUpsertQuickAccess = () => {
  const qry = useQueryClient();
  const userId = useUserId();
  const client = useGraphQLClient();

  return useMutation({
    mutationFn: async (qa: TQuickAccess) => {
      const mutate = await client.execute(quickAccessUpsertMutation, {
        object: {
          id: qa.id,
          user_id: qa.user_id,
          page_id: qa.page_id,
          product_id: qa.product_id,
          column_id: qa.column_id,
          shortcut_number: qa.shortcut_number,
        },
      });
      return mutate;
    },
    onMutate: (qa: TQuickAccess) => {
      qry.setQueryData(["quickAccess", userId], (old: TQuickAccess[]) => {
        if (!old) return [qa];
        const existing = old.find((x) => x.id === qa.id);
        if (existing) {
          return old.map((x) => (x.id === qa.id ? { ...x, ...qa } : x));
        }
        return [...old, qa];
      });
    },
    onSettled: () => qry.invalidateQueries({ queryKey: ["quickAccess", userId] }),
  });
};

export const addToFavouritesSection: MenuConfig[] = [
  {
    id: "add-to-favourites",
    label: "Add to Favourites",
    icon: <FileMagnifyingGlassIcon />,
    disabled: any(recipes.productOnly, selection.isMultiColumn),
    action: () => {},
    subMenu: new Array(5).fill(null).map((_, i) => {
      return {
        id: `favourite-${i + 1}`,
        label: (ctx) => {
          const existingQuickAccess = ctx.quickAccessData.find((q) => q.shortcut_number === i + 1);
          const displayName = existingQuickAccess?.productName;
          return displayName ? displayName : `Favourite ${i + 1}`;
        },
        shortcut: `Alt+Shift+${i + 1}`,
        action: (ctx) => () => {
          ctx.upsertQuickAccess.mutateAsync({
            id: `${ctx.userId}-${i + 1}`,
            user_id: ctx.userId,
            column_id: ctx.entityId,
            page_id: ctx.pageId,
            product_id: ctx.productId,
            shortcut_number: i + 1,
          });
        },
      };
    }),
  },
];
